import { useState } from "react";
import { useMutation } from "@apollo/client";
import toast from "react-hot-toast";
import { CREATE_SCHEDULE_BOOKING } from "../../graphql/operations/scheduleBooking";
import { trackLead } from "../../services/analytics";
import Turnstile from "./Turnstile";

interface ScheduleBookingFormProps {
    id?: string;
    title?: string;
    subtitle?: string;
    submitLabel?: string;
    className?: string;
}

export default function ScheduleBookingForm({
    id,
    title = "Book a Schedule",
    subtitle = "Schedule a meeting with us",
    submitLabel = "Book Schedule",
    className = "",
}: ScheduleBookingFormProps) {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phoneNo: "",
        date: "",
        time: "",
        notes: "",
    });

    const [captchaToken, setCaptchaToken] = useState<string>("");
    const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
    const [captchaEpoch, setCaptchaEpoch] = useState(0);

    const resetForm = () => {
        setFormData({
            name: "",
            email: "",
            phoneNo: "",
            date: "",
            time: "",
            notes: "",
        });
        setCaptchaToken("");
        setIsCaptchaVerified(false);
    };

    const handleCaptchaComplete = (token: string) => {
        setCaptchaToken(token);
        setIsCaptchaVerified(true);
    };

    const handleCaptchaReset = () => {
        setCaptchaToken("");
        setIsCaptchaVerified(false);
    };

    // The submitted token is spent either way, so both a second booking and a
    // retry after failure need a brand-new challenge rather than cleared state.
    const requestNewCaptcha = () => {
        handleCaptchaReset();
        setCaptchaEpoch((epoch) => epoch + 1);
    };

    const [createScheduleBooking, { loading: isSubmitting }] = useMutation(
        CREATE_SCHEDULE_BOOKING,
        {
            onCompleted: () => {
                trackLead();
                toast.success("Schedule booking submitted successfully! We'll get back to you soon.");
                resetForm();
                setCaptchaEpoch((epoch) => epoch + 1);
            },
            onError: (error) => {
                toast.error(error.message || "Failed to submit schedule booking. Please try again.");
                requestNewCaptcha();
            },
        }
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        if (name === "phoneNo") {
            setFormData((prev) => ({ ...prev, phoneNo: value.replace(/[^+\d\s\-()]/g, "") }));
            return;
        }

        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name || !formData.email || !formData.phoneNo || !formData.date || !formData.time) {
            toast.error("Please fill in all required fields.");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            toast.error("Please enter a valid email address.");
            return;
        }

        const selectedDate = new Date(formData.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate < today) {
            toast.error("Please select a future date.");
            return;
        }

        if (!isCaptchaVerified || !captchaToken) {
            toast.error("Please complete the CAPTCHA verification");
            return;
        }

        try {
            await createScheduleBooking({
                variables: {
                    input: {
                        name: formData.name,
                        email: formData.email,
                        phoneNo: formData.phoneNo,
                        date: formData.date,
                        time: formData.time,
                        notes: formData.notes || undefined,
                        captchaToken,
                    },
                },
            });
        } catch {
            // Handled by onError callback
        }
    };

    return (
        <div id={id} className={`relative group ${className}`.trim()}>
            <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-300" />
            <div className="relative bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                    <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-white">{title}</h3>
                        <p className="text-sm text-slate-400">{subtitle}</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
                                Name <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                                placeholder="Your name"
                            />
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                                Email <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                                placeholder="your.email@example.com"
                            />
                        </div>

                        <div>
                            <label htmlFor="phoneNo" className="block text-sm font-medium text-slate-300 mb-2">
                                Phone Number <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="tel"
                                id="phoneNo"
                                name="phoneNo"
                                value={formData.phoneNo}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                                placeholder="+1 234 567 8900"
                            />
                        </div>

                        <div>
                            <label htmlFor="date" className="block text-sm font-medium text-slate-300 mb-2">
                                Date <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="date"
                                id="date"
                                name="date"
                                value={formData.date}
                                onChange={handleChange}
                                required
                                min={new Date().toISOString().split("T")[0]}
                                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                            />
                        </div>

                        <div>
                            <label htmlFor="time" className="block text-sm font-medium text-slate-300 mb-2">
                                Time <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="time"
                                id="time"
                                name="time"
                                value={formData.time}
                                onChange={handleChange}
                                required
                                step="1800"
                                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="notes" className="block text-sm font-medium text-slate-300 mb-2">
                            Additional Notes (Optional)
                        </label>
                        <textarea
                            id="notes"
                            name="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            rows={4}
                            className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition resize-none"
                            placeholder="Any additional information or special requests..."
                        />
                    </div>

                    <div className="bg-slate-700/30 border border-slate-600/50 rounded-xl p-6">
                        <Turnstile
                            onComplete={handleCaptchaComplete}
                            onReset={handleCaptchaReset}
                            disabled={isSubmitting}
                            resetSignal={captchaEpoch}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting || !isCaptchaVerified}
                        className="w-full py-3 px-6 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                        {isSubmitting ? "Submitting..." : submitLabel}
                    </button>
                </form>
            </div>
        </div>
    );
}
